<template>
  <el-card v-loading="loading">
    <div id="page-panel">
      <div class="customer-top-nav">
        <div class="title">
          <span>{{ $t('reports.names.' + reportCode) }}</span><br>
        </div>
      </div>

      <el-form inline>
        <el-form-item :label="$t('reports.REP_RESERVATIONS.representative')">
          <el-select
            v-model="selectedRepId"
            :placeholder="$t('reports.REP_RESERVATIONS.selectRepresentative')"
            filterable
            :clearable="!isRepSelectionLocked"
            :disabled="isRepSelectionLocked"
            size="small"
            style="min-width: 320px;"
          >
            <el-option
              v-for="rep in availableReps"
              :key="rep.id"
              :label="`${rep.firstName} ${rep.lastName}`"
              :value="rep.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" size="small" :disabled="!selectedRepId" @click="fetchData">{{ $t('reports.REP_RESERVATIONS.display') }}</el-button>
        </el-form-item>
      </el-form>

      <el-divider />

      <h4>{{ $t('reports.REP_RESERVATIONS.allocations') }}</h4>

      <el-form inline class="mt-10">
        <el-form-item :label="$t('reports.REP_RESERVATIONS.productSearch')">
          <el-input
            v-model="productSearch"
            size="small"
            clearable
            :placeholder="$t('reports.REP_RESERVATIONS.searchProductPlaceholder')"
            :disabled="!filtersEnabled"
            @input="handleProductSearchChange"
            style="min-width: 280px;"
          />
        </el-form-item>
        <el-form-item>
          <el-button
            size="small"
            icon="el-icon-download"
            :disabled="!sortedRows.length"
            @click="downloadAllocationsExcel"
          >{{ $t('reports.REP_RESERVATIONS.downloadExcel') }}</el-button>
        </el-form-item>
      </el-form>

      <el-table
        :data="paginatedRows"
        :row-class-name="getRowClassName"
        @sort-change="handleSortChange"
        stripe
        border
        :empty-text="$t('reports.REP_RESERVATIONS.noAllocation')"
        style="width: 100%"
      >
        <el-table-column prop="id" :label="$t('reports.REP_RESERVATIONS.allocationNumber')" width="140">
          <template slot-scope="scope">
            <PortausLink :id="scope.row.id" path="/reservations/" :label="String(scope.row.id)" :openNewTab="true" />
          </template>
        </el-table-column>
        <el-table-column prop="productName" :label="$t('reports.REP_RESERVATIONS.product')" min-width="320" show-overflow-tooltip>
          <template slot-scope="scope">
            <el-link type="primary" @click="openProductDrawer(scope.row.puid)">{{ scope.row.productName }}</el-link>
            <div class="fs-12 secondary-text product-meta-line">{{ scope.row.producerName }}</div>
            <div class="fs-12 secondary-text product-meta-line">UVC: {{ scope.row.uvc }} | Volume: {{ scope.row.volume }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="createdDate" :label="$t('reports.REP_RESERVATIONS.allocationDate')" width="160" />
        <el-table-column prop="daysSinceAvailability" :label="$t('reports.REP_RESERVATIONS.nbDaysAvailable')" width="140" sortable="custom">
          <template slot-scope="scope">
            <span v-if="scope.row.daysSinceAvailability != null">
              {{ scope.row.daysSinceAvailability }}
              <span v-if="scope.row.availableLotsCount != null">({{ scope.row.availableLotsCount }} {{ scope.row.availableLotsCount > 1 ? $t('reports.REP_RESERVATIONS.lots') : $t('reports.REP_RESERVATIONS.lot') }})</span>
            </span>
            <span v-else>--</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('reports.REP_RESERVATIONS.progression')" min-width="280">
          <template slot-scope="scope">
            <el-progress
              :percentage="scope.row.progressPercentage"
              :status="scope.row.progressPercentage >= 100 ? 'success' : undefined"
            />
            <span class="fs-12 secondary-text">
              {{ scope.row.qtyUsed }} / {{ scope.row.qtyOriginal }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="completionDate" :label="$t('reports.REP_RESERVATIONS.completionDate')" width="180" />
        <el-table-column prop="forecastDate" :label="$t('reports.REP_RESERVATIONS.forecastDate')" width="190" />
      </el-table>

      <div v-if="showPagination" class="pagination-wrapper">
        <el-pagination
          background
          layout="prev, pager, next"
          :page-size="pageSize"
          :current-page="currentPage"
          :total="totalRows"
          @current-change="handlePageChange"
        />
      </div>

      <el-divider />
      <ReservationStatsCard v-if="dataLoaded" :repId="selectedRepId" class="mb-20" />
      <h4>{{ $t('reports.REP_RESERVATIONS.nonCompletedReservations') }}</h4>

      <el-form inline class="mt-10">
        <el-form-item :label="$t('reports.REP_RESERVATIONS.productSearch')">
          <el-input
            v-model="nonCompletedProductSearch"
            size="small"
            clearable
            :placeholder="$t('reports.REP_RESERVATIONS.searchProductPlaceholder')"
            :disabled="!nonCompletedFiltersEnabled"
            @input="handleNonCompletedProductSearchChange"
            style="min-width: 280px;"
          />
        </el-form-item>
        <el-form-item :label="$t('reports.REP_RESERVATIONS.delayFilter')">
          <el-checkbox-group v-model="delayBucketFilter" size="small" @change="nonCompletedCurrentPage = 1">
            <el-checkbox-button label="under120">
              <span class="delay-dot delay-dot--green"></span> {{ $t('reports.REP_RESERVATIONS.under120') }}
            </el-checkbox-button>
            <el-checkbox-button label="between120and150">
              <span class="delay-dot delay-dot--yellow"></span> {{ $t('reports.REP_RESERVATIONS.between120and150') }}
            </el-checkbox-button>
            <el-checkbox-button label="over150">
              <span class="delay-dot delay-dot--red"></span> {{ $t('reports.REP_RESERVATIONS.over150') }}
            </el-checkbox-button>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item>
          <el-button
            size="small"
            icon="el-icon-download"
            :disabled="!sortedNonCompletedReservationRows.length"
            @click="downloadReservationsExcel"
          >{{ $t('reports.REP_RESERVATIONS.downloadExcel') }}</el-button>
        </el-form-item>
      </el-form>

      <el-table
        :data="paginatedNonCompletedReservationRows"
        stripe
        border
        :empty-text="$t('reports.REP_RESERVATIONS.noNonCompletedReservation')"
        style="width: 100%"
        @sort-change="handleNonCompletedSortChange"
      >
        <el-table-column prop="id" :label="$t('reports.REP_RESERVATIONS.reservationNumber')" width="140">
          <template slot-scope="scope">
            <PortausLink :id="scope.row.id" path="/reservations/" :label="String(scope.row.id)" :openNewTab="true" />
          </template>
        </el-table-column>
        <el-table-column prop="customerName" :label="$t('reports.REP_RESERVATIONS.customer')" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            <PortausLink v-if="scope.row.customerId" :id="scope.row.customerId" path="/customers/" :label="scope.row.customerName" :openNewTab="true" />
            <span v-else>{{ scope.row.customerName }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="productName" :label="$t('reports.REP_RESERVATIONS.product')" min-width="320" show-overflow-tooltip>
          <template slot-scope="scope">
            <el-link type="primary" @click="openProductDrawer(scope.row.puid)">{{ scope.row.productName }}</el-link>
            <div class="fs-12 secondary-text product-meta-line">{{ scope.row.producerName }}</div>
            <div class="fs-12 secondary-text product-meta-line">UVC: {{ scope.row.uvc }} | Volume: {{ scope.row.volume }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="reservationDate" :label="$t('reports.REP_RESERVATIONS.reservationDate')" width="160" sortable="custom" />
        <el-table-column :label="$t('reports.REP_RESERVATIONS.progression')" min-width="280" sortable="custom" prop="progressPercentage">
          <template slot-scope="scope">
            <el-progress
              :percentage="scope.row.progressPercentage"
              :status="scope.row.progressPercentage >= 100 ? 'success' : undefined"
            />
            <span class="fs-12 secondary-text">
              {{ scope.row.qtyUsed }} / {{ scope.row.qtyReserved }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="daysSinceAvailability" :label="$t('reports.REP_RESERVATIONS.nbDaysDelay')" width="140" sortable="custom">
          <template slot-scope="scope">
            <span v-if="scope.row.daysSinceAvailability != null">{{ scope.row.daysSinceAvailability }}</span>
            <span v-else>--</span>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="showNonCompletedPagination" class="pagination-wrapper">
        <el-pagination
          background
          layout="prev, pager, next"
          :page-size="nonCompletedPageSize"
          :current-page="nonCompletedCurrentPage"
          :total="totalNonCompletedRows"
          @current-change="handleNonCompletedPageChange"
        />
      </div>

      <ProductDetailDrawer
        :puid="selectedPuid"
        direction="ltr"
        :visible="productDetailDrawerVisible"
        @close="closeProductDetailDrawer"
      />
    </div>
  </el-card>
</template>

<script>
import reservationAPI from '@/services/API/reservation';
import InventoryAPI from '@/services/API/inventory';
import PortausLink from '@/components/portaus-link';
import ProductDetailDrawer from '@/views/pages/inventory/components/product-detail-drawer';
import ReservationStatsCard from '@/components/reports/ReservationStatsCard';
import Fuse from 'fuse.js/dist/fuse.common';
import dayjs from 'dayjs';
import XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'rep-reservations-report',
  components: {
    PortausLink,
    ProductDetailDrawer,
    ReservationStatsCard,
  },
  data() {
    return {
      loading: false,
      dataLoaded: false,
      selectedRepId: null,
      rows: [],
      currentPage: 1,
      pageSize: 10,
      nonCompletedCurrentPage: 1,
      nonCompletedPageSize: 10,
      selectedPuid: null,
      productDetailDrawerVisible: false,
      productSearch: '',
      sortProp: null,
      sortOrder: null,
      nonCompletedSortProp: null,
      nonCompletedSortOrder: null,
      nonCompletedReservationRows: [],
      nonCompletedProductSearch: '',
      delayBucketFilter: [],
    };
  },
  computed: {
    reportCode() {
      return this.$route.params.code;
    },
    isRepSelectionLocked() {
      return this.isStrictFilteredByRep === true && this.isCurrentUserRep === true && !this.isCurrentUserAdmin;
    },
    availableReps() {
      if (this.isRepSelectionLocked) {
        return this.filterUserWithRoleRep.filter(rep => Number(rep.id) === Number(this.getCurrentUserId));
      }
      return this.filterUserWithRoleRep;
    },
    filteredRows() {
      return this.rows.filter(row => !row.delayed);
    },
    searchedRows() {
      const query = _.trim(this.productSearch || '');
      if (query === '') {
        return this.filteredRows;
      }

      const fuse = new Fuse(this.filteredRows, {
        includeScore: true,
        threshold: 0.35,
        ignoreLocation: true,
        keys: [
          { name: 'productName', weight: 0.65 },
          { name: 'producerName', weight: 0.2 },
          { name: 'uvc', weight: 0.075 },
          { name: 'volume', weight: 0.075 },
        ],
      });

      return fuse.search(query).map(result => result.item);
    },
    sortedRows() {
      if (this.sortProp === 'daysSinceAvailability' && this.sortOrder != null) {
        const direction = this.sortOrder === 'ascending' ? 'asc' : 'desc';
        return _.orderBy(
          this.searchedRows,
          [
            row => (_.get(row, 'daysSinceAvailability') == null ? 1 : 0),
            row => Number(_.get(row, 'daysSinceAvailability', 0)),
            row => dayjs(_.get(row, 'allocationDateRaw')).valueOf(),
          ],
          ['asc', direction, 'desc']
        );
      }

      return _.orderBy(this.searchedRows, ['allocationDateRaw', 'id'], ['desc', 'desc']);
    },
    paginatedRows() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.sortedRows.slice(start, start + this.pageSize);
    },
    totalRows() {
      return this.sortedRows.length;
    },
    filteredNonCompletedRows() {
      let rows = this.nonCompletedReservationRows.filter(row => !row.delayed);
      if (this.delayBucketFilter.length > 0) {
        rows = rows.filter(row => {
          const days = row.daysSinceAvailability;
          if (days == null) return false;
          return this.delayBucketFilter.some(bucket => {
            if (bucket === 'under120') return days < 120;
            if (bucket === 'between120and150') return days >= 120 && days <= 150;
            if (bucket === 'over150') return days > 150;
            return false;
          });
        });
      }
      return rows;
    },
    searchedNonCompletedRows() {
      const query = _.trim(this.nonCompletedProductSearch || '');
      if (query === '') {
        return this.filteredNonCompletedRows;
      }
      const fuse = new Fuse(this.filteredNonCompletedRows, {
        includeScore: true,
        threshold: 0.35,
        ignoreLocation: true,
        keys: [
          { name: 'productName', weight: 0.65 },
          { name: 'producerName', weight: 0.2 },
          { name: 'uvc', weight: 0.075 },
          { name: 'volume', weight: 0.075 },
        ],
      });
      return fuse.search(query).map(result => result.item);
    },
    sortedNonCompletedReservationRows() {
      const prop = this.nonCompletedSortProp;
      const order = this.nonCompletedSortOrder;
      if (prop && order) {
        const dir = order === 'ascending' ? 'asc' : 'desc';
        const nullsLast = row => (_.get(row, prop) == null ? 1 : 0);
        if (prop === 'reservationDate') {
          return _.orderBy(this.searchedNonCompletedRows, [r => r.reservationDateRaw, 'id'], [dir, dir]);
        }
        return _.orderBy(
          this.searchedNonCompletedRows,
          [nullsLast, row => _.get(row, prop), 'id'],
          ['asc', dir, dir]
        );
      }
      return _.orderBy(this.searchedNonCompletedRows, ['reservationDateRaw', 'id'], ['desc', 'desc']);
    },
    paginatedNonCompletedReservationRows() {
      const start = (this.nonCompletedCurrentPage - 1) * this.nonCompletedPageSize;
      return this.sortedNonCompletedReservationRows.slice(start, start + this.nonCompletedPageSize);
    },
    totalNonCompletedRows() {
      return this.sortedNonCompletedReservationRows.length;
    },
    showNonCompletedPagination() {
      return this.totalNonCompletedRows > this.nonCompletedPageSize;
    },
    hasAllocations() {
      return this.rows.length > 0;
    },
    filtersEnabled() {
      return this.selectedRepId != null && this.hasAllocations;
    },
    nonCompletedFiltersEnabled() {
      return this.selectedRepId != null && this.nonCompletedReservationRows.length > 0;
    },
    showPagination() {
      return this.totalRows > this.pageSize;
    },
    ...mapGetters('users', ['filterUserWithRoleRep']),
    ...mapGetters(['isStrictFilteredByRep', 'isCurrentUserRep', 'isCurrentUserAdmin', 'getCurrentUserId']),
  },
  methods: {
    formatDate(date) {
      if (!date) return '--';
      return dayjs(date).format('YYYY-MM-DD');
    },
    getSalesOrdersArray(allocation) {
      const salesOrders = _.get(allocation, 'salesOrders');
      if (Array.isArray(salesOrders)) return salesOrders;
      if (salesOrders == null) return [];
      return [salesOrders];
    },
    getCompletionDate(allocation, progressPercentage) {
      if (progressPercentage < 100) {
        return '--';
      }

      const salesOrders = this.getSalesOrdersArray(allocation)
        .filter(order => _.get(order, 'createdAt') != null)
        .sort((a, b) => dayjs(_.get(b, 'createdAt')).valueOf() - dayjs(_.get(a, 'createdAt')).valueOf());

      const latestOrder = salesOrders[0];
      return latestOrder ? this.formatDate(_.get(latestOrder, 'createdAt')) : '--';
    },
    getForecastDate(allocation, qtyUsed, availableQty, progressPercentage) {
      if (progressPercentage >= 100) {
        return '--';
      }

      const salesOrders = this.getSalesOrdersArray(allocation)
        .filter(order => _.get(order, 'createdAt') != null)
        .sort((a, b) => dayjs(_.get(a, 'createdAt')).valueOf() - dayjs(_.get(b, 'createdAt')).valueOf());

      if (salesOrders.length === 0 || qtyUsed <= 0 || availableQty <= 0) {
        return '--';
      }

      const firstSaleDate = dayjs(_.get(salesOrders[0], 'createdAt'));
      const lastSaleDate = dayjs(_.get(salesOrders[salesOrders.length - 1], 'createdAt'));
      const elapsedDays = Math.max(1, lastSaleDate.diff(firstSaleDate, 'day'));
      const velocityPerDay = qtyUsed / elapsedDays;

      if (!Number.isFinite(velocityPerDay) || velocityPerDay <= 0) {
        return '--';
      }

      const remainingDays = Math.ceil(availableQty / velocityPerDay);
      const forecastDate = dayjs().add(remainingDays, 'day');
      return this.formatDate(forecastDate);
    },
    buildRow(allocation, delayInfoByPuid = {}) {
      const qtyOriginal = Number(_.get(allocation, 'qty', 0)) || 0;
      const availableQty = Number(_.get(allocation, 'availableQty', _.get(allocation, 'quantity.available', 0))) || 0;
      const qtyUsed = Math.max(0, qtyOriginal - availableQty);
      const progressPercentage = qtyOriginal > 0
        ? Math.min(100, Math.max(0, Math.round((qtyUsed / qtyOriginal) * 100)))
        : 0;
      const puid = _.get(allocation, 'puid', _.get(allocation, 'product.puid', null));
      const delayed = _.get(allocation, 'delayed', false) === true;
      const daysSinceAvailability = delayed ? null : _.get(delayInfoByPuid, [puid, 'nbDays'], null);
      const availableLotsCount = delayed ? null : _.get(delayInfoByPuid, [puid, 'availableLotsCount'], null);

      return {
        id: allocation.id,
        puid,
        delayed,
        daysSinceAvailability,
        availableLotsCount,
        productName: _.get(allocation, 'product.name', '--'),
        producerName: _.get(allocation, 'product.provider.name', '--'),
        uvc: _.get(allocation, 'product.uvc', '--'),
        volume: _.get(allocation, 'product.volume', '--'),
        createdDate: this.formatDate(_.get(allocation, 'date')),
        allocationDateRaw: _.get(allocation, 'date'),
        qtyOriginal,
        qtyUsed,
        progressPercentage,
        completionDate: this.getCompletionDate(allocation, progressPercentage),
        forecastDate: this.getForecastDate(allocation, qtyUsed, availableQty, progressPercentage),
      };
    },
    buildNonCompletedReservationRow(reservation, delayInfoByPuid = {}) {
      const puid = _.get(reservation, 'puid', _.get(reservation, 'product.puid', null));
      const qtyReserved = _.get(reservation, 'quantity.reserved', _.get(reservation, 'qty', null));
      const qtyRemaining = _.get(reservation, 'quantity.available', _.get(reservation, 'availableQty', null));
      const qtyUsed = (qtyReserved != null && qtyRemaining != null) ? Math.max(0, qtyReserved - qtyRemaining) : null;
      const progressPercentage = (qtyReserved != null && qtyReserved > 0 && qtyUsed != null)
        ? Math.min(100, Math.max(0, Math.round((qtyUsed / qtyReserved) * 100)))
        : 0;
      const usualName = _.trim(_.get(reservation, 'customer.usualName', ''));
      const customerName = usualName !== '' ? usualName : _.get(reservation, 'customer.name', '--');

      return {
        id: reservation.id,
        puid,
        customerId: _.get(reservation, 'customer.id', null),
        customerName,
        productName: _.get(reservation, 'product.name', '--'),
        producerName: _.get(reservation, 'product.provider.name', '--'),
        uvc: _.get(reservation, 'product.uvc', '--'),
        volume: _.get(reservation, 'product.volume', '--'),
        reservationDate: this.formatDate(_.get(reservation, 'date')),
        reservationDateRaw: _.get(reservation, 'date'),
        qtyReserved,
        qtyRemaining,
        qtyUsed,
        progressPercentage,
        daysSinceAvailability: _.get(delayInfoByPuid, [puid, 'nbDays'], null),
        delayed: _.get(delayInfoByPuid, [puid, 'nbDays'], null) == null,
      };
    },
    getAvailableLotsCountFromProduct(product) {
      const items = _.get(product, 'items', []);
      if (!Array.isArray(items) || items.length === 0) {
        return null;
      }

      return items.filter((item) => {
        const onHand = Number(_.get(item, 'quantity.onHand', _.get(item, 'quantity.on_hand', _.get(item, 'qty', 0))));
        return Number.isFinite(onHand) && onHand > 0;
      }).length;
    },
    async fetchDelayInfoByPuid(puids) {
      if (!Array.isArray(puids) || puids.length === 0) {
        return {};
      }

      const products = await InventoryAPI.fetchProductsV2({ puid: puids, paging: 'no' });
      const map = {};
      const puidsToEnrich = [];

      _.forEach(products, (product) => {
        const puid = _.get(product, 'puid');
        if (!puid) {
          return;
        }

        const nbDays = _.get(product, 'quantity.nbDays', _.get(product, 'quantity.nb_days', _.get(product, 'nbDays')));
        const availableLotsCount = this.getAvailableLotsCountFromProduct(product);
        map[puid] = {
          nbDays: nbDays != null ? Number(nbDays) : null,
          availableLotsCount,
        };

        if (availableLotsCount == null) {
          puidsToEnrich.push(puid);
        }
      });

      if (puidsToEnrich.length > 0) {
        const details = await Promise.all(puidsToEnrich.map((puid) => InventoryAPI.getInventoryProductV2(puid).catch(() => null)));

        details.forEach((productDetail) => {
          const puid = _.get(productDetail, 'puid');
          if (!puid) {
            return;
          }
          const availableLotsCount = this.getAvailableLotsCountFromProduct(productDetail);
          if (availableLotsCount != null) {
            map[puid] = {
              ...map[puid],
              availableLotsCount,
            };
          }
        });
      }

      return map;
    },
    getRowClassName({ row }) {
      if (row.delayed) {
        return 'delayed-row';
      }
      return '';
    },
    handleProductSearchChange() {
      this.currentPage = 1;
    },
    handlePageChange(page) {
      this.currentPage = page;
    },
    handleNonCompletedPageChange(page) {
      this.nonCompletedCurrentPage = page;
    },
    handleNonCompletedSortChange({ prop, order }) {
      this.nonCompletedSortProp = prop;
      this.nonCompletedSortOrder = order;
      this.nonCompletedCurrentPage = 1;
    },
    handleNonCompletedProductSearchChange() {
      this.nonCompletedCurrentPage = 1;
    },
    handleSortChange({ prop, order }) {
      this.sortProp = prop;
      this.sortOrder = order;
      this.currentPage = 1;
    },
    downloadAllocationsExcel() {
      const rows = this.sortedRows.map(row => ({
        [this.$t('reports.REP_RESERVATIONS.allocationNumber')]: row.id,
        [this.$t('reports.REP_RESERVATIONS.product')]: row.productName,
        [this.$t('reports.REP_RESERVATIONS.producer')]: row.producerName,
        'UVC': row.uvc,
        [this.$t('reports.REP_RESERVATIONS.volume')]: row.volume,
        [this.$t('reports.REP_RESERVATIONS.allocationDate')]: row.createdDate,
        [this.$t('reports.REP_RESERVATIONS.nbDaysAvailable')]: row.daysSinceAvailability != null ? row.daysSinceAvailability : '',
        [this.$t('reports.REP_RESERVATIONS.progression')]: `${row.qtyUsed} / ${row.qtyOriginal}`,
        '%': row.progressPercentage,
        [this.$t('reports.REP_RESERVATIONS.completionDate')]: row.completionDate,
        [this.$t('reports.REP_RESERVATIONS.forecastDate')]: row.forecastDate,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, this.$t('reports.REP_RESERVATIONS.allocations'));
      const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
      saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `allocations-${dayjs().format('YYYY-MM-DD')}.xlsx`);
    },
    downloadReservationsExcel() {
      const rows = this.sortedNonCompletedReservationRows.map(row => ({
        [this.$t('reports.REP_RESERVATIONS.reservationNumber')]: row.id,
        [this.$t('reports.REP_RESERVATIONS.customer')]: row.customerName,
        [this.$t('reports.REP_RESERVATIONS.product')]: row.productName,
        [this.$t('reports.REP_RESERVATIONS.producer')]: row.producerName,
        'UVC': row.uvc,
        [this.$t('reports.REP_RESERVATIONS.volume')]: row.volume,
        [this.$t('reports.REP_RESERVATIONS.reservationDate')]: row.reservationDate,
        [this.$t('reports.REP_RESERVATIONS.qtyReserved')]: row.qtyReserved != null ? row.qtyReserved : '',
        [this.$t('reports.REP_RESERVATIONS.qtyRemaining')]: row.qtyRemaining != null ? row.qtyRemaining : '',
        '%': row.progressPercentage,
        [this.$t('reports.REP_RESERVATIONS.nbDaysDelay')]: row.daysSinceAvailability != null ? row.daysSinceAvailability : '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, this.$t('reports.REP_RESERVATIONS.nonCompletedReservations'));
      const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
      saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `reservations-${dayjs().format('YYYY-MM-DD')}.xlsx`);
    },
    openProductDrawer(puid) {
      if (!puid) {
        this.$message.warning(this.$t('reports.REP_RESERVATIONS.noPuid'));
        return;
      }
      this.selectedPuid = puid;
      this.productDetailDrawerVisible = true;
    },
    closeProductDetailDrawer() {
      this.selectedPuid = null;
      this.productDetailDrawerVisible = false;
    },
    applyStrictRepFilter() {
      if (!this.isRepSelectionLocked) {
        return;
      }

      const currentRep = _.find(this.filterUserWithRoleRep, rep => Number(rep.id) === Number(this.getCurrentUserId));
      if (currentRep != null) {
        this.selectedRepId = currentRep.id;
      } else if (this.getCurrentUserId != null) {
        this.selectedRepId = this.getCurrentUserId;
      }
    },
    async fetchData() {
      if (!this.selectedRepId) return;

      this.loading = true;
      try {
        const [allocationsResponse, allReservationsResponse] = await Promise.all([
          reservationAPI.fetchRepresentativeAllocationsReport({
            alloc: true,
            repId: this.selectedRepId,
            paging: 'false',
            orderBy: 'createdAt',
            direction: 'DESC',
          }),
          reservationAPI.fetchReservationsV2({
            repId: this.selectedRepId,
            paging: 'false',
            orderBy: 'createdAt',
            direction: 'DESC',
            intent: false,
            delayed: false,
            alloc: false,
            active: true,
          }),
        ]);

        const allocationRows = Array.isArray(allocationsResponse) ? allocationsResponse : [];
        const allReservations = Array.isArray(allReservationsResponse) ? allReservationsResponse : [];
        const nonCompletedReservations = allReservations.filter(r =>
          _.get(r, 'completed') !== true &&
          _.get(r, 'intent') !== true &&
          _.get(r, 'delayed') !== true &&
          _.get(r, 'alloc') !== true
        );

        const puids = _.chain(allocationRows)
          .map((allocation) => _.get(allocation, 'puid', _.get(allocation, 'product.puid', null)))
          .concat(_.chain(nonCompletedReservations)
            .map((reservation) => _.get(reservation, 'puid', _.get(reservation, 'product.puid', null)))
            .value())
          .filter(Boolean)
          .uniq()
          .value();

        const delayInfoByPuid = await this.fetchDelayInfoByPuid(puids);

        this.rows = allocationRows.map(allocation => this.buildRow(allocation, delayInfoByPuid));
        this.nonCompletedReservationRows = nonCompletedReservations.map(reservation => this.buildNonCompletedReservationRow(reservation, delayInfoByPuid));
        this.currentPage = 1;
        this.dataLoaded = true;
      } catch (error) {
        this.$message.error(this.$t('reports.REP_RESERVATIONS.loadError'));
      } finally {
        this.loading = false;
      }
    },
    ...mapActions('users', ['fetchAdminUsers']),
  },
  created() {
    this.fetchAdminUsers().then(() => {
      this.applyStrictRepFilter();
      if (this.selectedRepId) {
        return this.fetchData();
      }
      return null;
    });
  },
};
</script>

<style lang="scss" scoped>
@import '../../../../../assets/scss/_variables';

.title {
  font-size: 1.6em;
  font-weight: bold;
  margin-right: 20px;
  position: relative;
  float: left;
}

.customer-top-nav {
  height: 40px;
}

.pagination-wrapper {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.product-meta-line {
  line-height: 1.1;
  margin: 0;
}

::v-deep .delayed-row td {
  background-color: #f2f3f5 !important;
  color: #909399;
}

.delay-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;

  &--green  { background-color: #67c23a; }
  &--yellow { background-color: #e6a23c; }
  &--red    { background-color: #f56c6c; }
}
</style>
